import { EnvironmentOutlined, FilePdfOutlined, LoadingOutlined } from '@ant-design/icons';
import { OrderTranscription } from 'config/constants/transcription.constant';
import React, { useState } from 'react';
import { Purchase } from 'store/models/order.model';
import { User } from 'store/models/user.model';
import styles from './style.module.scss';
import { Card } from 'antd';
import ProductList from 'catalog/product/components/product-list.component';
import Totalizer from '../totalizer.component';
import OrderUtils from 'config/utils/order.utils';
import { SnackbarUtilities } from 'config/utils/snackbar.util';

type PurchaseDetailsProps = {
  purchase: Purchase;
  user: User;
  withNextSteps: boolean;
};

const PurchaseDetails: React.FC<PurchaseDetailsProps> = ({ purchase, user, withNextSteps }) => {
  const [isOpeningBoleta, setIsOpeningBoleta] = useState(false);

  const handleOpenBoleta = async (): Promise<void> => {
    if (!purchase.boletaPdfUrl || isOpeningBoleta) return;

    setIsOpeningBoleta(true);
    try {
      const fileName = purchase.boletaFolio ? `boleta-${purchase.boletaFolio}.pdf` : `boleta-${purchase.code}.pdf`;
      await OrderUtils.openPdfFromUrl(purchase.boletaPdfUrl, fileName);
    } catch {
      SnackbarUtilities.error(OrderTranscription.ERROR_DOWNLOAD_BOLETA);
    } finally {
      setIsOpeningBoleta(false);
    }
  };

  return (
    <div className={styles['purchase-details-layout']}>
      <div className={styles['main-content']}>
        <div className={styles['order-info-header']}>
          <div className={styles['info-card']}>
            <span className={styles['label']}>{OrderTranscription.PAYMENT_DATE}</span>
            <span className={styles['value']}>{purchase.date}</span>
          </div>
          <div className={styles['info-card']}>
            <span className={styles['label']}>{OrderTranscription.PAYMENT_METHOD}</span>
            <span className={styles['value']}>{purchase.paymentType}</span>
          </div>
          <div className={styles['info-card']}>
            <span className={styles['label']}>
              {purchase.deliveryStatusTitle ||
                (purchase.isDelivery ? OrderTranscription.DELIVERY_STATUS : OrderTranscription.DELIVERY_TYPE)}
            </span>
            <span className={styles['value-tag']}>
              {purchase.deliveryStatusLabel || OrderUtils.getPurchaseStatusText(purchase.deliveryStatus)}
            </span>
          </div>
        </div>
        {purchase.isDelivery && (
          <div className={styles['address-content']}>
            <div className={styles['icon-wrapper']}>
              <EnvironmentOutlined />
            </div>
            <div className={styles['address-text']}>
              <p>{purchase.address}</p>
            </div>
          </div>
        )}
        {purchase.boletaPdfUrl && (
          <button
            type='button'
            className={styles['receipt-content']}
            onClick={handleOpenBoleta}
            disabled={isOpeningBoleta}
            aria-busy={isOpeningBoleta}
          >
            <div className={styles['icon-wrapper']}>
              {isOpeningBoleta ?
                <LoadingOutlined />
              : <FilePdfOutlined />}
            </div>
            <div className={styles['receipt-text']}>
              <span>{OrderTranscription.BOLETA_ELECTRONICA}</span>
              <p>
                {purchase.boletaFolio ? `${OrderTranscription.BOLETA_FOLIO} ${purchase.boletaFolio}` : OrderTranscription.DOWNLOAD_BOLETA}
              </p>
            </div>
            <span className={styles['receipt-action']}>
              {isOpeningBoleta ? OrderTranscription.OPENING_BOLETA : OrderTranscription.DOWNLOAD_BOLETA}
            </span>
          </button>
        )}
        {purchase.buyerNotes && (
          <Card title={OrderTranscription.BUYER_NOTES} className={styles['notes-card']}>
            <p>{purchase.buyerNotes}</p>
          </Card>
        )}
        {withNextSteps && (
          <div className={styles['next-steps']}>
            <h3>{OrderTranscription.NEXT_STEPS_TITLE}</h3>
            <ul>
              <li>{OrderTranscription.NEXT_STEPS_1}</li>
              <li>{purchase.isDelivery ? OrderTranscription.NEXT_STEPS_DELIVERY : OrderTranscription.NEXT_STEPS_PICKUP}</li>
              <li>
                {OrderTranscription.NEXT_STEPS_EMAIL_NOTE} <strong>{user.email}</strong>
              </li>
            </ul>
          </div>
        )}
        <div className={styles['section-item']}>
          <Card>
            <div className={styles['scrollable-products']}>
              <ProductList items={purchase.items} userProfile={user} isAuthenticated={true} />
            </div>
          </Card>
        </div>
      </div>
      <div className={styles['sidebar-content']}>
        <div className={styles['sticky-sidebar']}></div>
        <div className={styles['sticky-sidebar']}>
          <Totalizer
            totalFormatted={purchase.totalFormatted}
            subTotalFormatted={purchase.subTotalFormatted}
            taxes={purchase.taxes}
            discounts={purchase.discounts}
          />
        </div>
      </div>
    </div>
  );
};

export default PurchaseDetails;
